import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import clsx from 'clsx';
import { mainNavigation, footerNavigation } from '@/data/navigation';
import { Newsletter } from './Newsletter';
import { CookieConsent } from './CookieConsent';

export function MarketingLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-accent focus:text-white focus:rounded-lg focus:outline-none"
      >
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="flex-1" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  return (
    <header
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-colors',
        isHomePage ? 'bg-transparent' : 'bg-paper/95 backdrop-blur-sm border-b border-gray-200'
      )}
    >
      <div className="container-marketing">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
              <span className="text-white font-bold text-lg">O</span>
            </div>
            <span className={clsx('font-heading font-bold text-xl', isHomePage ? 'text-paper' : 'text-ink')}>
              Omniforge
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
            {mainNavigation.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {item.children ? (
                  <>
                    <button
                      className={clsx(
                        'flex items-center gap-1 text-sm font-medium transition-colors',
                        isHomePage ? 'text-paper/90 hover:text-paper' : 'text-ink/80 hover:text-ink'
                      )}
                      aria-expanded={openDropdown === item.label}
                      aria-haspopup="true"
                      onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    >
                      {item.label}
                      <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                    <AnimatePresence>
                      {openDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-2 w-56 py-2 bg-white rounded-xl shadow-elevated"
                          role="menu"
                          aria-orientation="vertical"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block px-4 py-2 text-sm text-ink hover:bg-cream transition-colors"
                              role="menuitem"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={clsx(
                      'text-sm font-medium transition-colors',
                      isHomePage ? 'text-paper/90 hover:text-paper' : 'text-ink/80 hover:text-ink'
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="https://app.getomniforge.com/login"
              className={clsx(
                'text-sm font-medium transition-colors',
                isHomePage ? 'text-paper/90 hover:text-paper' : 'text-ink/80 hover:text-ink'
              )}
            >
              Log In
            </a>
            <Link to="/demo" className="btn-primary py-2.5 px-5 text-sm">
              Request Demo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <XIcon className={clsx('w-6 h-6', isHomePage ? 'text-paper' : 'text-ink')} />
            ) : (
              <MenuIcon className={clsx('w-6 h-6', isHomePage ? 'text-paper' : 'text-ink')} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-200 overflow-hidden"
          >
            <div className="container-marketing py-4">
              <nav className="space-y-4">
                {mainNavigation.map((item) => (
                  <div key={item.label}>
                    {item.children ? (
                      <div className="space-y-2">
                        <span className="text-sm font-semibold text-muted">{item.label}</span>
                        <div className="pl-4 space-y-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="block text-ink py-1"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className="block text-ink py-2 font-medium"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <a
                    href="https://app.getomniforge.com/login"
                    className="block text-ink py-2 font-medium"
                  >
                    Log In
                  </a>
                  <Link
                    to="/demo"
                    className="btn-primary w-full"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Request Demo
                  </Link>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-ink text-paper" role="contentinfo">
      {/* Social Proof Bar */}
      <div className="border-b border-gray-800">
        <div className="container-marketing py-8">
          <div className="text-center mb-6">
            <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">
              Trusted by 500+ service companies
            </p>
          </div>

          {/* Customer Logos Placeholder */}
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="w-24 h-8 bg-gray-700/50 rounded flex items-center justify-center"
                aria-label={`Customer logo ${i}`}
              >
                <span className="text-gray-500 text-xs">Logo {i}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="border-b border-gray-800">
        <div className="container-marketing py-12">
          <div className="max-w-xl mx-auto">
            <Newsletter variant="footer" />
          </div>
        </div>
      </div>

      {/* Footer Links */}
      <div className="container-marketing py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                <span className="text-white font-bold text-lg">O</span>
              </div>
              <span className="font-heading font-bold text-xl">Omniforge</span>
            </Link>
            <p className="text-gray-400 text-sm mb-2">
              The operating system for service companies.
            </p>
            <p className="text-accent font-semibold text-sm italic mb-4">
              Forge something great.
            </p>
{/* TODO: Update social links to real Omniforge accounts when available */}
            <div className="flex gap-4">
              <a href="https://linkedin.com" className="text-gray-400 hover:text-paper transition-colors" aria-label="LinkedIn" rel="noopener noreferrer" target="_blank">
                <LinkedInIcon className="w-5 h-5" aria-hidden="true" />
              </a>
              <a href="https://facebook.com" className="text-gray-400 hover:text-paper transition-colors" aria-label="Facebook" rel="noopener noreferrer" target="_blank">
                <FacebookIcon className="w-5 h-5" aria-hidden="true" />
              </a>
              <a href="https://youtube.com" className="text-gray-400 hover:text-paper transition-colors" aria-label="YouTube" rel="noopener noreferrer" target="_blank">
                <YouTubeIcon className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Product</h4>
            <ul className="space-y-2">
              {footerNavigation.product.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-gray-400 hover:text-paper text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries - spans 2 columns on large screens */}
          <div className="col-span-2">
            <h4 className="font-heading font-semibold mb-4">Industries</h4>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
              {footerNavigation.industries.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-gray-400 hover:text-paper text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {footerNavigation.company.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-gray-400 hover:text-paper text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Resources</h4>
            <ul className="space-y-2">
              {footerNavigation.resources.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-gray-400 hover:text-paper text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Compare */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Compare</h4>
            <ul className="space-y-2">
              {footerNavigation.compare.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="text-gray-400 hover:text-paper text-sm transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} Omniforge. Built for builders.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-gray-400 hover:text-paper text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-paper text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Icons
function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
